import React, { useState, useEffect } from 'react';
import { forumAPI, projectAPI, wardAPI, forumCommentsAPI } from '../services/api';
import { ForumPost, Project, Ward } from '../types';

const ForumPage: React.FC = () => {
  const [posts, setPosts] = useState<ForumPost[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [wards, setWards] = useState<Ward[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('discussions');
  const [postSuccess, setPostSuccess] = useState(false);
  const [commentingOn, setCommentingOn] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [newPost, setNewPost] = useState({
    title: '',
    content: '',
    category: 'general' as 'general' | 'project' | 'service' | 'budget' | 'complaint',
    project_id: '',
    ward: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [postsRes, projectsRes, wardsRes] = await Promise.all([
        forumAPI.getPosts(),
        projectAPI.getPublic('Turkana'),
        wardAPI.getByCounty('Turkana')
      ]);
      
      setPosts(postsRes.data);
      setProjects(projectsRes.data);
      setWards(wardsRes.data);
    } catch (error) {
      console.error('Failed to load forum data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitPost = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await forumAPI.createPost(newPost);
      setNewPost({ title: '', content: '', category: 'general' as any, project_id: '', ward: '' });
      setPostSuccess(true);
      setTimeout(() => setPostSuccess(false), 3000);
      loadData();
    } catch (error) {
      console.error('Failed to create post:', error);
    }
  };

const handleLike = async (postId: string) => {
    try {
      await forumCommentsAPI.like(postId);
      loadData();
    } catch (error) {
      console.error('Failed to like post:', error);
    }
  };

  const handleAddComment = async (postId: string) => {
    if (!commentText.trim()) return;
    try {
      await forumCommentsAPI.addComment(postId, { content: commentText, author: 'Community Member' });
      setCommentText('');
      setCommentingOn(null);
      loadData();
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const toggleComments = (postId: string) => {
    setExpandedComments(prev => {
      const next = new Set(prev);
      next.has(postId) ? next.delete(postId) : next.add(postId);
      return next;
    });
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'project': return '🏗️';
      case 'service': return '🏛️';
      case 'budget': return '💰';
      case 'complaint': return '⚠️';
      default: return '💬';
    }
  };

  if (loading) return <div className="loading">Loading forum...</div>;

  return (
    <div className="forum-page">
      <header className="page-header">
        <div className="icon icon-primary">🗣️</div>
        <h1>Community Forum</h1>
        <p>Discuss county projects, services, and share feedback with the community</p>
      </header>

      <div className="forum-tabs">
        <button 
          className={activeTab === 'discussions' ? 'active' : ''}
          onClick={() => setActiveTab('discussions')}
        >
          💬 Discussions
        </button>
        <button 
          className={activeTab === 'create' ? 'active' : ''}
          onClick={() => setActiveTab('create')}
        >
          ✏️ New Post
        </button>
      </div>

      {activeTab === 'discussions' && (
        <section className="discussions-section">
          <div className="forum-stats">
            <div className="stat-card">
              <h3>{posts.length}</h3>
              <p>Total Posts</p>
            </div>
            <div className="stat-card">
              <h3>{posts.reduce((sum, p) => sum + p.comments.length, 0)}</h3>
              <p>Comments</p>
            </div>
            <div className="stat-card">
              <h3>{new Set(posts.map(p => p.author)).size}</h3>
              <p>Active Users</p>
            </div>
          </div>

          <div className="posts-list">
            {posts.map(post => (
              <div key={post.id} className="forum-post">
                <div className="post-header">
                  <div className="post-meta">
                    <span className="category-icon">{getCategoryIcon(post.category)}</span>
                    <span className="category">{post.category.toUpperCase()}</span>
                    {post.ward && <span className="ward-tag">📍 {post.ward}</span>}
                  </div>
                  <div className="post-date">
                    {new Date(post.created_at).toLocaleDateString()}
                  </div>
                </div>

                <div className="post-content">
                  <h3>{post.title}</h3>
                  <p>{post.content}</p>
                  
                  {post.project_id && (
                    <div className="related-project">
                      <span>🏗️ Related Project: {projects.find(p => p.id === post.project_id)?.name}</span>
                    </div>
                  )}
                </div>

                <div className="post-footer">
                  <div className="post-author">
                    <span>👤 {post.author}</span>
                  </div>
                  <div className="post-actions">
                    <button className="like-btn" onClick={() => handleLike(post.id)}>
                      👍 {post.likes || 0}
                    </button>
                    <button className="comment-btn" onClick={() => toggleComments(post.id)}>
                      💬 {post.comments.length} {expandedComments.has(post.id) ? '▲' : '▼'}
                    </button>
                  </div>
                </div>

                {expandedComments.has(post.id) && (
                  <div className="comments-section">
                    {post.comments.length > 0 && (
                      <>
                        <h4>Comments ({post.comments.length})</h4>
                        {post.comments.map(comment => (
                          <div key={comment.id} className="comment">
                            <div className="comment-header">
                              <span className="comment-author">👤 {comment.author}</span>
                              <span className="comment-date">{new Date(comment.created_at).toLocaleDateString()}</span>
                            </div>
                            <p>{comment.content}</p>
                          </div>
                        ))}
                      </>
                    )}
                    {commentingOn === post.id ? (
                      <div style={{ marginTop: '0.8rem' }}>
                        <textarea
                          value={commentText}
                          onChange={e => setCommentText(e.target.value)}
                          placeholder="Write a comment..."
                          rows={2}
                          style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc', marginBottom: '0.5rem' }}
                        />
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button className="btn btn-primary" style={{ fontSize: '0.85rem' }} onClick={() => handleAddComment(post.id)}>Post Comment</button>
                          <button className="btn btn-secondary" style={{ fontSize: '0.85rem' }} onClick={() => { setCommentingOn(null); setCommentText(''); }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button
                        style={{ marginTop: '0.5rem', fontSize: '0.85rem', background: 'none', border: '1px solid #ccc', borderRadius: '4px', padding: '0.3rem 0.8rem', cursor: 'pointer' }}
                        onClick={() => { setCommentingOn(post.id); setCommentText(''); }}
                      >✏️ Add Comment</button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

{activeTab === 'create' && (
        <section className="create-post-section">
          <h2>Create New Discussion</h2>
          
          <form onSubmit={handleSubmitPost} className="post-form">
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={newPost.title}
                onChange={(e) => setNewPost({...newPost, title: e.target.value})}
                required
                placeholder="Enter discussion title..."
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Category</label>
                <select
                  value={newPost.category}
                  onChange={(e) => setNewPost({...newPost, category: e.target.value as any})}
                >
                  <option value="general">General Discussion</option>
                  <option value="project">Project Feedback</option>
                  <option value="service">Service Delivery</option>
                  <option value="budget">Budget Concerns</option>
                  <option value="complaint">Complaint</option>
                </select>
              </div>

              <div className="form-group">
                <label>Ward</label>
                <select
                  value={newPost.ward}
                  onChange={(e) => setNewPost({...newPost, ward: e.target.value})}
                >
                  <option value="">Select Ward (Optional)</option>
                  {wards.map(ward => (
                    <option key={ward.id} value={ward.name}>{ward.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {newPost.category === 'project' && (
              <div className="form-group">
                <label>Related Project</label>
                <select
                  value={newPost.project_id}
                  onChange={(e) => setNewPost({...newPost, project_id: e.target.value})}
                >
                  <option value="">Select Project (Optional)</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>{project.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Content *</label>
              <textarea
                value={newPost.content}
                onChange={(e) => setNewPost({...newPost, content: e.target.value})}
                required
                rows={6}
                placeholder="Share your thoughts, feedback, or concerns..."
              />
            </div>

            <button type="submit" className="submit-btn">
              📝 Post Discussion
            </button>
            {postSuccess && (
              <p style={{ color: 'green', marginTop: '8px' }}>✅ Post published successfully!</p>
            )}
          </form>
        </section>
      )}
    </div>
  );
};

export default ForumPage;